import { useMemo, useRef, useState } from 'react';
import Modal from './Modal';
import { paymentsApi, formatCurrencyWhole } from '../utils/api';
import { toast } from './Toast';
import { track, EVENTS } from '../utils/analytics';
import Icon from './Icon';
import { PAYMENT_METHOD_OPTIONS } from '../pages/SupplierManagementPage';
import { rangeLabel } from '../utils/paymentLifecycle';
import { roundWhole } from '../utils/money';
import { afterFailedTry, isRefusal, newRequestKey } from '../utils/requestResult';

// Multi-supplier payment: record one payment (uniform method + date) across
// several selected suppliers at once. For each supplier the backend pays all
// its eligible unpaid docs in the active date range — the same logic as the
// single supplier-level PaymentModal, just batched. Mirrors PaymentModal's
// form; adds a confirmation summary listing the selected suppliers + grand
// total before saving.
//
// Same shared list as PaymentModal — see the note there on why this is derived
// rather than hand-written.
const METHODS = PAYMENT_METHOD_OPTIONS.map(o => o.value);
const todayISO = () => new Date().toISOString().split('T')[0];

export default function MultiPaymentModal({ suppliers, dateFrom, dateTo, onSuccess, onClose }) {
  const [form, setForm] = useState({ payment_method: METHODS[0], payment_date: todayISO(), notes: '' });
  const [saving, setSaving] = useState(false);
  const [approveMonth, setApproveMonth] = useState(false);
  // Same as PaymentModal (#391): one key for every try. Past `open` a try may have
  // recorded, so a retry sends what that try sent: the fields lock from the moment a
  // try goes out, and the request itself is kept in case the page's props move
  // meanwhile. Closing reloads the page. No closing mid-try.
  const [requestKey] = useState(newRequestKey);
  const [tryState, setTryState] = useState('open');
  const resultSettled = tryState === 'open';
  const sentPayload = useRef(null);
  const close = () => {
    if (saving) return;
    (resultSettled ? onClose : onSuccess)();
  };

  // Approve-while-paying, batched: offered when the shared range names one
  // month and at least one selected supplier is not yet approved for it. The
  // server applies it per supplier over that supplier's own window (the
  // due-date filter can give each a different one), approving every month in
  // it that holds a document; this counts only who would actually be approved.
  const { label: monthLabel, multi } = rangeLabel(dateFrom, dateTo);
  const unapproved = monthLabel ? suppliers.filter(s => s.lifecycle && !s.lifecycle.approved_by) : [];
  const approvalOffer = unapproved.length > 0 ? { monthLabel, multi, count: unapproved.length } : null;

  // What this payment will actually settle: the amount STILL OWED, matching the
  // "לתשלום" column the user selected these rows from (SuppliersPage uses the
  // same `amount_unpaid ?? total_amount` formula).
  //
  // It must not be total_amount. That is the full invoice value including any
  // part already paid, so for a supplier on שולם חלקית the confirmation showed a
  // LARGER figure than the list behind it — at the exact moment the user is
  // verifying a batch financial write. The server only inserts payments for docs
  // with no existing payment row (`NOT EXISTS (SELECT 1 FROM payments …)` in
  // paySupplierLevel), so the owed figure is also what actually gets recorded —
  // which is what the note under this summary has always claimed.
  const owed = (s) => parseFloat(s.amount_unpaid ?? s.total_amount) || 0;

  // Each supplier is its own transfer of a whole-shekel amount (owner,
  // 14/10/2026), so the total is the sum of the rows AS SHOWN — three suppliers
  // at ₪100.50 are 101 + 101 + 101 = 303, not a rounded 301.50 = 302 that would
  // read as an arithmetic error on the confirmation.
  const grandTotal = useMemo(
    () => suppliers.reduce((sum, s) => sum + roundWhole(owed(s)), 0),
    [suppliers]
  );

  const buildPayload = () => {
    // Each supplier carries its own date window (s._range), so the docs paid
    // match exactly the docs/total shown — even under a due-date filter where
    // the window differs per supplier. Fall back to the shared dateFrom/To.
    const payload = {
      items: suppliers.map(s => ({
        supplier_id: s.id,
        date_from: s._range?.date_from ?? dateFrom ?? undefined,
        date_to:   s._range?.date_to   ?? dateTo   ?? undefined,
      })),
      idempotency_key: requestKey,
      ...form,
    };
    if (approvalOffer && approveMonth) payload.approve_month = true;
    return payload;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = resultSettled ? buildPayload() : sentPayload.current;
      sentPayload.current = payload;
      const r = await paymentsApi.createBulk(payload);
      const { docCount = 0, supplierCount = 0, skipped = [], approvedCount = 0, replayed = false } = r?.data || {};
      // Nothing was actually recorded (every selected supplier had no eligible
      // docs). Tell the user and keep the modal open instead of a false success.
      if (docCount === 0) {
        toast('אין מסמכים פתוחים לתשלום עבור הספקים שנבחרו', 'error');
        // A clear answer: this key wrote nothing, on this try or any before it.
        setTryState('open');
        setSaving(false);
        return;
      }
      track(EVENTS.BULK_PAYMENT_RECORDED, {
        supplier_count: supplierCount,
        doc_count: docCount,
        skipped_count: skipped.length,
        payment_method: form.payment_method,
      });
      let msg = replayed ? 'התשלומים כבר נרשמו בניסיון הקודם' : `נרשם תשלום עבור ${docCount} מסמכים אצל ${supplierCount} ספקים`;
      if (skipped.length) msg += ` (${skipped.length} ספקים דולגו — אין מסמכים לתשלום)`;
      if (approvedCount > 0) msg += ` · ${approvedCount} ספקים אושרו לתשלום`;
      toast(msg);
      onSuccess();
    } catch (err) {
      if (isRefusal(err)) toast(err.response.data?.error || 'שגיאה ברישום תשלום', 'error');
      setTryState(state => afterFailedTry(state, err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal onClose={close} title="תשלום מרובה" size={480}
      closeOnBackdrop={false} style={{ maxHeight: '90vh', overflowY: 'auto' }}>

    {/* Confirmation summary: which suppliers, and the grand total. */}
    <div style={{ background: '#f7f9fc', borderRadius: 10, padding: '12px 16px', marginBottom: 16 }}>
      <div style={{ fontWeight: 700, color: '#1a1a2e', marginBottom: 8 }}>
        {suppliers.length} ספקים נבחרו
      </div>
      <div style={{ maxHeight: 180, overflowY: 'auto' }}>
        {suppliers.map(s => (
          <div key={s.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', color: 'var(--ink-muted)', padding: '3px 0' }}>
            <span>{s.name}</span>
            <span style={{ fontWeight: 600 }}>{formatCurrencyWhole(owed(s))}</span>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #e2e8f0', marginTop: 8, paddingTop: 8, fontWeight: 700, color: '#1a1a2e' }}>
        <span>סכום כולל</span>
        <span>{formatCurrencyWhole(grandTotal)}</span>
      </div>
      <div style={{ fontSize: '0.78rem', color: 'var(--ink-muted)', marginTop: 8 }}>
        יירשם תשלום על כל המסמכים הלא-משולמים של כל ספק בטווח המוצג
        {(dateFrom || dateTo) && ` (${dateFrom || '…'} עד ${dateTo || '…'})`}.
      </div>
    </div>

    <form onSubmit={handleSubmit}>
      <fieldset disabled={saving || !resultSettled} style={{ border: 0, margin: 0, padding: 0, minWidth: 0 }}>
      <div className="form-group">
        <label>אמצעי תשלום</label>
        <select className="form-control" value={form.payment_method}
          onChange={e => setForm(f => ({ ...f, payment_method: e.target.value }))}>
          {METHODS.map(m => <option key={m}>{m}</option>)}
        </select>
      </div>
      <div className="form-group">
        <label>תאריך תשלום</label>
        <input type="date" className="form-control" value={form.payment_date} required
          onChange={e => setForm(f => ({ ...f, payment_date: e.target.value }))} />
      </div>
      <div className="form-group">
        <label>הערות (אופציונלי)</label>
        <textarea className="form-control" rows={2} value={form.notes}
          placeholder="הערות לגבי התשלום..."
          onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
      </div>
      {approvalOffer && (
        <div className="form-group">
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontWeight: 500 }}>
            <input type="checkbox" checked={approveMonth} onChange={e => setApproveMonth(e.target.checked)} />
            <span>
              אשר לתשלום {approvalOffer.multi ? 'לחודשים' : 'לחודש'} {approvalOffer.monthLabel}
              {approvalOffer.count < suppliers.length ? ` (${approvalOffer.count} ספקים שטרם אושרו)` : ''}
            </span>
          </label>
          <div style={{ fontSize: '0.78rem', color: 'var(--ink-muted)', marginTop: 4 }}>
            יסומן "מאושר לתשלום" על שמך, ויירשם כהערה על כל ספק.
          </div>
        </div>
      )}
      </fieldset>
      {tryState === 'unknown' && (
        <div role="alert" style={{ fontSize: '0.85rem', color: 'var(--warning-ink)', marginBottom: 12 }}>
          לא התקבלה תשובה מהשרת, ולא ברור אם התשלומים נרשמו. אפשר לנסות שוב: הם לא יירשמו פעמיים.
        </div>
      )}
      <div className="modal-actions">
        <button type="button" className="btn btn-secondary" onClick={close} disabled={saving}>ביטול</button>
        {tryState !== 'conflict' && (
          <button type="submit" className="btn btn-success" disabled={saving}>
            {saving ? 'שומר...' : <><Icon name="check" size={16} /> {resultSettled ? `אשר תשלום ל-${suppliers.length} ספקים` : 'נסה שוב'}</>}
          </button>
        )}
      </div>
    </form>
    </Modal>
  );
}
